/**
 * Store Pinia des analyses de production (composition API)
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

import { IProductionAnalyse } from '../models/productionAnalyse';
import { IProductionAnalyseApiResponse } from '../models/api/productionAnalyseApiResponse';
import { IProductionAnalyseFilter } from '../models/filters/productionAnalyseFilter';
import { IProductionAnalyseForm } from '../models/forms/productionAnalyseForm';
import ProductionAnalyseServices from '../services/ProductionAnalyseService';
import { ServiceOperationResult } from '../models/api/serviceOperationResult';
import { IDataOperationResult } from '../models/api/dataOperationResult';
import DataOperationError from '../models/api/dataOperationError';
import { IParameter } from '../models/parameter';
import { IBlade } from '../models/blade';
import { IAgent } from '../models/agent';
import { ILot } from '../models/lot';
import { numberJourBetween } from '../helper/dateFormatter';
import { useLotStore } from './lot.store';
import { useParameterStore } from './parameter.store';
import { useAgentStore } from './agent.store';
import { useBladeStore } from './blade.store';
import signalRManager from "../helper/signalRManager";
import IPayloadStore from "../models/api/payloadStore";
import i18n from "@/i18n";
import { usePerformanceTracker } from "../composables/tools/PerformanceComposable";

/**
 * Store Pinia des analyses de production utilisant la Composition API
 */
export const useProductionAnalyseStore = defineStore('ProductionAnalyses', () => {
    // State
    const datas = ref<IProductionAnalyse[]>([]);
    const current = ref<IProductionAnalyse>({} as IProductionAnalyse);
    const itemForm = ref<IProductionAnalyseForm>({} as IProductionAnalyseForm);
    const lotId = ref(0);
    const hasOnlyControl = ref(false);
    const loadedCenterId = ref<number | undefined>(undefined);
    // Fenetre de dates reellement couverte par datas : permet a une vue de sauter un rechargement
    // quand le cache couvre deja sa periode (meme role que lot.store/manufactoringProduction.store).
    const loadedDateFrom = ref<Date | undefined>(undefined);

    // Getters

    const getLastControleProduction = computed(() => (idProduction: number): IProductionAnalyse | undefined => {
        const stateDatas = datas.value
            .filter((item) => item.lot?.production?.id === idProduction && item.isControl)
            .sort((a, b) => new Date(b.dateAnalyse).getTime() - new Date(a.dateAnalyse).getTime());
        return stateDatas[0];
    });

    const getLastObservationProduction = computed(() => (idProduction: number): IProductionAnalyse | undefined => {
        const stateDatas = datas.value
            .filter((item) => item.lot?.production?.id === idProduction && !item.isControl)
            .sort((a, b) => new Date(b.dateAnalyse).getTime() - new Date(a.dateAnalyse).getTime());
        return stateDatas[0];
    });

    const getLastDayProduction = computed(() => (idProduction: number): number => {
        let jourOffset = -1;
        const stateDatas = datas.value
            .filter((item) => item.lot?.production?.id === idProduction && item.isControl)
            .sort((a, b) => new Date(b.dateAnalyse).getTime() - new Date(a.dateAnalyse).getTime());

        if (stateDatas.length > 0) {
            jourOffset = numberJourBetween(stateDatas[0].lot?.production?.dateProduction ?? '', stateDatas[0].dateAnalyse);
        }

        return jourOffset;
    });

    const getLastDayLot = computed(() => (idLot: number): number => {
        let jourOffset = -1;
        const stateDatas = datas.value
            .filter((item) => item.lot?.id === idLot && item.isControl)
            .sort((a, b) => new Date(b.dateAnalyse).getTime() - new Date(a.dateAnalyse).getTime());

        if (stateDatas.length > 0) {
            jourOffset = numberJourBetween(stateDatas[0].lot?.dateLot ?? '', stateDatas[0].dateAnalyse);
        }

        return jourOffset;
    });

    const getSortedByDateControle = computed(() => (onlyControl: boolean = false): IProductionAnalyse[] => {
        return datas.value
            .filter(x => (!onlyControl || x.isControl))
            .sort((a, b) => new Date(b.dateAnalyse).getTime() - new Date(a.dateAnalyse).getTime());
    });

    const getSortedByDateControleAndProduction = computed(() => (productionId: number, onlyControl: boolean = false): IProductionAnalyse[] => {
        return datas.value
            .filter(x => x.lot?.production?.id === productionId && (!onlyControl || x.isControl))
            .sort((a, b) => new Date(b.dateAnalyse).getTime() - new Date(a.dateAnalyse).getTime());
    });

    const getSortedByDateControleAndLot = computed(() => (lotIdParam: number, onlyControl: boolean = false): IProductionAnalyse[] => {
        return datas.value
            .filter(x => x.lot?.id === lotIdParam && (!onlyControl || x.isControl))
            .sort((a, b) => new Date(b.dateAnalyse).getTime() - new Date(a.dateAnalyse).getTime());
    });

    /**
     * Pour un lot monospermie : retourne le dernier contrôle ≤ J+5 pour chacune
     * des 6 dernières productions de l'animal, trié de la plus récente à la plus ancienne.
     */
    const getAnimalHistoriqueControles = computed(() => (animalId: number): IProductionAnalyse[] => {
        // Tous les contrôles à péremption de cet animal
        const allControls = datas.value.filter(x =>
            x.isControl &&
            x.lot?.production?.animal?.id === animalId
        );

        // Grouper par production
        const productionMap = new Map<number, IProductionAnalyse[]>();
        for (const control of allControls) {
            const prodId = control.lot?.production?.id;
            if (prodId == null) continue;
            if (!productionMap.has(prodId)) productionMap.set(prodId, []);
            productionMap.get(prodId)!.push(control);
        }

        // Trier les productions par date décroissante, garder les 6 dernières
        const sortedProductions = Array.from(productionMap.entries())
            .map(([, controls]) => ({
                dateProduction: new Date(controls[0].lot?.production?.dateProduction ?? 0),
                controls,
            }))
            .sort((a, b) => b.dateProduction.getTime() - a.dateProduction.getTime())
            .slice(0, 6);

        // Pour chaque production, le dernier contrôle à J+5 max
        const result: IProductionAnalyse[] = [];
        for (const { controls } of sortedProductions) {
            const validControls = controls
                .filter(c => {
                    const refDate = c.lot?.production?.dateProduction ?? c.lot.dateLot;
                    return numberJourBetween(refDate, c.dateAnalyse) <= 5;
                })
                .sort((a, b) => new Date(b.dateAnalyse).getTime() - new Date(a.dateAnalyse).getTime());

            if (validControls.length > 0) {
                result.push(validControls[0]);
            }
        }

        return result;
    });

    const getByDayOffset = computed(() => (animalId: number, offsets: number[], excludeId?: number): IProductionAnalyse | null => {
        const analyses = datas.value.filter((analyse: IProductionAnalyse) =>
            analyse.lot?.production?.animal?.id === animalId &&
            (excludeId == null || analyse.id !== excludeId)
        );

        const filteredAnalyses = analyses.filter((analyse: IProductionAnalyse) => {
            const offset = numberJourBetween(analyse.lot?.production?.dateProduction ?? analyse.lot.dateLot, analyse.dateAnalyse);
            return offsets.includes(offset);
        });

        const sortedAnalyses = filteredAnalyses.sort((a, b) => new Date(b.dateAnalyse).getTime() - new Date(a.dateAnalyse).getTime());
        return sortedAnalyses[0] || null;
    });

    // Actions

    /**
     * Hydrate une liste d'analyses de production en batch (optimisé pour la performance)
     * - Charge les stores dépendants une seule fois en parallèle
     * - Crée des Maps d'index pour des lookups O(1)
     *
     * @param analyses Liste d'analyses reçues de l'API avec des IDs
     * @returns Liste d'analyses avec les références des stores
     */
    async function hydrateProductionAnalyses(analyses: IProductionAnalyseApiResponse[]): Promise<IProductionAnalyse[]> {
        if (analyses.length === 0) return [];

        const lotStore = useLotStore();
        const parameterStore = useParameterStore();
        const agentStore = useAgentStore();
        const bladeStore = useBladeStore();

        // Charger les stores dépendants en parallèle si nécessaire
        const loadPromises: Promise<unknown>[] = [];
        if (parameterStore.datas.length === 0) {
            loadPromises.push(parameterStore.load({ page: 1, pageSize: 0 }));
        }
        if (agentStore.datas.length === 0) {
            loadPromises.push(agentStore.load({ page: 1, pageSize: 0 }));
        }
        if (bladeStore.datas.length === 0) {
            loadPromises.push(bladeStore.load({ page: 1, pageSize: 0 }));
        }
        if (loadPromises.length > 0) {
            await Promise.all(loadPromises);
        }

        // Maps O(1)
        const lotMap = new Map(lotStore.datas.map(l => [l.id, l]));
        const paramMap = new Map(parameterStore.datas.map(p => [p.id, p]));
        const agentMap = new Map(agentStore.datas.map(a => [a.id, a]));
        const bladeMap = new Map(bladeStore.datas.map(b => [b.id, b]));

        // Hydrater en une seule passe
        const models: IProductionAnalyse[] = [];
        for (const a of analyses) {
            const model = { ...a } as unknown as IProductionAnalyse;

            model.lot = lotMap.get(a.lotId) ?? model.lot;
            model.typeAnomaly = a.typeAnomalyId ? paramMap.get(a.typeAnomalyId) : undefined;
            model.agent = agentMap.get(a.agentId) ?? model.agent;
            model.blade = a.bladeId ? bladeMap.get(a.bladeId)! : model.blade;
            model.conservationAptitude = a.conservationAptitudeId ? paramMap.get(a.conservationAptitudeId) : undefined;

            models.push(model);
        }
        return models;
    }

    /**
     * Chargement des données dans le store
     * @param filter Le filtre à appliquer pour le chargement des données
     */
    async function load(filter: IProductionAnalyseFilter, centerId?: number, perfPage?: string) {
        const { start, end } = usePerformanceTracker(perfPage);

        start('GET /ProductionAnalyse (réseau)');
        const result = await ProductionAnalyseServices.getAll<IProductionAnalyseFilter, IProductionAnalyseApiResponse>(filter);
        end('GET /ProductionAnalyse (réseau)', 'api');
        datas.value = [];

        if (!result.error) {
            start('hydrateProductionAnalyses()');
            datas.value = await hydrateProductionAnalyses(result.data as IProductionAnalyseApiResponse[]);
            end('hydrateProductionAnalyses()', 'mapping');
        }

        loadedCenterId.value = centerId;
        loadedDateFrom.value = filter.dateFrom;
        return result;
    }

    /**
     * Récupération d'un élément par son identifiant
     */
    async function get(id: number) {
        const result = await ProductionAnalyseServices.getById<IProductionAnalyseApiResponse>(id);

        if (!result.error) {
            const [modelData] = await hydrateProductionAnalyses([result.data as IProductionAnalyseApiResponse]);
            const index = datas.value.findIndex((item) => item.id === id);
            if (index !== -1) {
                datas.value[index] = modelData;
            }
            current.value = modelData;
            itemForm.value = toForm(modelData);
        }

        return result;
    }

    /**
     * Sauvegarde d'un élément
     */
    async function save(maj: boolean = true, isPatch: boolean = false) {
        let result: ServiceOperationResult<IProductionAnalyseApiResponse | IDataOperationResult<IProductionAnalyseApiResponse>>;

        if (itemForm.value.id > 0) {
            result = await ProductionAnalyseServices.update(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateProductionAnalyses([result.data as IProductionAnalyseApiResponse]);
                current.value = modelData;
                const index = datas.value.findIndex((item) => item.id === itemForm.value.id);
                if (index !== -1) {
                    datas.value[index] = current.value;
                } else {
                    const error = new DataOperationError();
                    error.REQUEST_ERROR = i18n.global.t("errors.itemNotFoundInStore");
                    return new ServiceOperationResult<IDataOperationResult<IProductionAnalyse>>(true, { errors: error });
                }
            }
        } else {
            result = await ProductionAnalyseServices.create(itemForm.value);
            if (!result.error) {
                const [modelData] = await hydrateProductionAnalyses([result.data as IProductionAnalyseApiResponse]);
                current.value = modelData;
                // SignalR peut avoir déjà ajouté l'item avant la réponse HTTP (race condition via _hub.CreateItem)
                if (datas.value.findIndex((a) => a.id == current.value?.id) == -1) {
                    datas.value.push(current.value);
                }
            }
        }

        return result;
    }

    /**
     * Mappage de l'élément current en formulaire
     */
    function toForm(item: IProductionAnalyse): IProductionAnalyseForm {
        const form = {} as IProductionAnalyseForm;
        Object.assign(form, item);
        if (item.typeAnomaly)
            form.typeAnomalyId = item.typeAnomaly.id;
        if (item.agent)
            form.agentId = item.agent.id;
        if (item.blade)
            form.bladeId = item.blade.id;
        if (item.conservationAptitude)
            form.conservationAptitudeId = item.conservationAptitude.id;
        if (item.lot)
            form.lotId = item.lot.id;
        return form;
    }

    /**
     * Création d'un nouvel élément en formulaire
     */
    function newForm(lienId: number): IProductionAnalyseForm {
        itemForm.value = {
            id: 0,
            lotId: lienId,
            prcLiving: 0,
            motility: 0,
            prcAnomaly: 0,
            typeAnomalyId: 0,
            isUsedCasa: false,
            prcUtile: 0,
            prcMotility: 0,
            prcNormal: 0,
            subject: '',
            reference: 0,
            prcProgress: 0,
            prcTotal: 0,
            proximalDroplet: 0,
            distalDroplet: 0,
            totalmml: 0,
            numberTotal: 0,
            curvedTails: 0,
            ejaculatedVolume: 0,
            numberMotility: 0,
            numberNormal: 0,
            numberUtile: 0,
            bladeId: 0,
            comment: '',
            dateAnalyse: new Date(),
            agentId: 0,
            conservationAptitudeId: 0,
        } as IProductionAnalyseForm;
        return itemForm.value;
    }

    /**
     * Création d'un nouvel élément courant
     */
    function newItem(lien: ILot): IProductionAnalyse {
        current.value = {
            id: 0,
            lot: lien,
            prcLiving: 0,
            motility: 0,
            prcAnomaly: 0,
            typeAnomaly: { id: 0 } as IParameter,
            isUsedCasa: false,
            prcUtile: 0,
            prcMotility: 0,
            prcNormal: 0,
            subject: '',
            reference: 0,
            prcProgress: 0,
            prcTotal: 0,
            proximalDroplet: 0,
            distalDroplet: 0,
            totalmml: 0,
            numberTotal: 0,
            curvedTails: 0,
            ejaculatedVolume: 0,
            numberMotility: 0,
            numberNormal: 0,
            numberUtile: 0,
            blade: { id: 0 } as IBlade,
            comment: '',
            dateAnalyse: new Date(),
            agent: { id: 0 } as IAgent,
            conservationAptitude: { id: 0 } as IParameter,
        } as IProductionAnalyse;
        return current.value;
    }

    return {
        // State
        datas,
        current,
        itemForm,
        lotId,
        hasOnlyControl,
        loadedCenterId,
        loadedDateFrom,

        // Getters
        getLastControleProduction,
        getLastObservationProduction,
        getLastDayProduction,
        getLastDayLot,
        getSortedByDateControle,
        getSortedByDateControleAndProduction,
        getSortedByDateControleAndLot,
        getAnimalHistoriqueControles,
        getByDayOffset,

        // Actions
        hydrateProductionAnalyses,
        load,
        get,
        save,
        toForm,
        newForm,
        newItem,
    };
});

signalRManager.connection.on("MajProductionAnalyse", async (payload: IPayloadStore) => {
    const store = useProductionAnalyseStore();
    const apiData = payload.data as IProductionAnalyseApiResponse;

    switch (payload.action) {
        case "Create": {
            if (store.datas.findIndex((a) => a.id == apiData?.id) == -1) {
                const [hydrated] = await store.hydrateProductionAnalyses([apiData]);
                store.datas.push(hydrated);
                // Synchroniser vers lot.analyses[]
                syncAnalyseToLot(apiData.lotId, hydrated);
            }
            break;
        }
        case "Update": {
            const idx = store.datas.findIndex((a) => a.id == apiData?.id);
            if (idx > -1) {
                const [hydrated] = await store.hydrateProductionAnalyses([apiData]);
                store.datas.splice(idx, 1, hydrated);
                // Synchroniser vers lot.analyses[]
                syncAnalyseToLot(apiData.lotId, hydrated);
            }
            break;
        }
        case "Delete": {
            const idx = store.datas.findIndex((a) => a.id == payload.id);
            if (idx > -1) store.datas.splice(idx, 1);
            break;
        }
    }
});

/** Synchronise une analyse vers lotStore.datas[].analyses[] */
function syncAnalyseToLot(lotId: number, analyse: IProductionAnalyse) {
    if (!lotId) return;
    const lotStore = useLotStore();
    const lot = lotStore.datas.find(l => l.id === lotId);
    if (!lot) return;
    if (!lot.analyses) lot.analyses = [];
    const idx = lot.analyses.findIndex(a => a.id === analyse.id);
    if (idx > -1) {
        lot.analyses.splice(idx, 1, analyse);
    } else {
        lot.analyses.push(analyse);
    }
}
